import { useCutActivityQuery } from '@features/redux/apis/cut';
import { useMemo, useState } from 'react';
import { MaterialIdMap } from '@consts/materials';
import { CutReportLayout } from '@components/reports/CutReportLayout';
import { EmployeeEffects } from './EmployeeEffects';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const EMPLOYEE_ACTIVITY_PAGE_SIZE = 30;

const EMPLOYEE_ACTIVITY_HEADERS = [
    'Manufacturer',
    'Model',
    'Coverage',
    'Material',
    'Cut Date',
    'Sync Date',
    'Cut Type',
    'Cut Status',
];

type EmployeeCutActivityCardProps = {
    employeeEmail: string;
    locationId: number;
    startDate?: string;
    endDate?: string;
};

export const EmployeeCutActivityCard = ({ employeeEmail, locationId, ...dates }: EmployeeCutActivityCardProps) => {
    const [pageNum, setPageNum] = useState(1);

    const allTime = !(dates.startDate && dates.endDate);

    const queryState = useCutActivityQuery({
        ...dates,
        pageSize: EMPLOYEE_ACTIVITY_PAGE_SIZE,
        pageNum: pageNum - 1,
        employeeEmail,
        locationIds: `${locationId}`,
    });
    const { data: cutActivity, isFetching: loading } = queryState;

    const totalCuts = +(cutActivity?.[0]?.totalCutsInQuery as any) || 0;
    const pageCount = Math.ceil(totalCuts / EMPLOYEE_ACTIVITY_PAGE_SIZE);

    const tableRows = useMemo(
        () =>
            cutActivity?.map((cut) => [
                cut.manufacturer,
                cut.model,
                cut.coverage,
                MaterialIdMap[cut.materialId as keyof typeof MaterialIdMap]?.[0] || 'Unknown',
                dayjs(cut.cutDate).format('MM/DD/YYYY HH:mm'),
                dayjs(cut.syncDate).format('MM/DD/YYYY HH:mm'),
                cut.cutType,
                cut.cutStatus,
            ]) || [],
        [cutActivity]
    );

    return (
        <>
            <EmployeeEffects queryState={queryState} typeOfData='cut activity' />

            <CutReportLayout
                tableHeight='clamp(50px, 50dvh, 200px)'
                sx={{ marginTop: '0px !important' }}
                pageNum={pageNum}
                numPages={pageCount}
                setPageNum={setPageNum}
                hasData={Boolean(tableRows?.length)}
                loading={loading}
                title='Employee Cut Activity'
                rangeText={allTime ? 'All time' : `${dates.startDate} - ${dates.endDate}`}
                tableOptions={{
                    headers: EMPLOYEE_ACTIVITY_HEADERS,
                    data: tableRows,
                }}
            />
        </>
    );
};
